// NSARA Stores V2 — sélecteur de boutique (section 8 de la mission).
//
// Le titre de la boutique ("First Class ▼") EST le sélecteur: un clic ouvre une
// liste des boutiques de l'entreprise, avec la boutique active cochée, un
// séparateur puis "+ Créer une boutique".
//
// Accessibilité (section 26): bouton avec aria-haspopup/aria-expanded, liste en
// role="menu", fermeture sur Escape et clic extérieur, navigation au clavier
// (flèches haut/bas, Entrée/Espace), focus géré.
import React, { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { shopMonogram } from './storeUtils';

export default function ShopSwitcher({ shops, activeShopId, onSelect, onCreate, disabled }) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const baseId = useId();

  const activeShop = (shops || []).find((shop) => shop.id === activeShopId) || (shops || [])[0] || null;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      const index = (shops || []).findIndex((shop) => shop.id === activeShopId);
      setCursor(index >= 0 ? index : 0);
      window.setTimeout(() => listRef.current?.querySelector('[role="menuitem"]')?.focus(), 0);
    }
  }, [open, shops, activeShopId]);

  const choose = (shopId) => {
    setOpen(false);
    if (shopId !== activeShopId) onSelect?.(shopId);
  };

  const onKeyDown = (event) => {
    const count = (shops || []).length;
    if (event.key === 'Escape') { event.preventDefault(); setOpen(false); return; }
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setOpen(true); }
      return;
    }
    if (event.key === 'ArrowDown') { event.preventDefault(); setCursor((c) => (count === 0 ? 0 : (c + 1) % count)); }
    if (event.key === 'ArrowUp') { event.preventDefault(); setCursor((c) => (count === 0 ? 0 : (c - 1 + count) % count)); }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const shop = (shops || [])[cursor];
      if (shop) choose(shop.id);
    }
  };

  useEffect(() => {
    if (!open) return;
    const items = [...(listRef.current?.querySelectorAll('[role="menuitem"]') || [])];
    items[cursor]?.focus();
  }, [cursor, open]);

  if (!activeShop) return null;

  return (
    <div className="stores-switcher" ref={rootRef}>
      <button
        type="button"
        id={`${baseId}-trigger`}
        className="stores-switcher-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${baseId}-menu` : undefined}
        aria-label={`Boutique active : ${activeShop.name}. Changer de boutique.`}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
      >
        <span className="stores-switcher-mark" aria-hidden="true">
          {activeShop.logo_url
            ? <img src={activeShop.logo_url} alt="" />
            : shopMonogram(activeShop.name)}
        </span>
        <span className="stores-switcher-name">{activeShop.name}</span>
        <ChevronDown size={16} aria-hidden="true" className={open ? 'stores-switcher-caret is-open' : 'stores-switcher-caret'} />
      </button>

      {open && (
        <div className="stores-switcher-menu" id={`${baseId}-menu`} role="menu" aria-labelledby={`${baseId}-trigger`} ref={listRef} onKeyDown={onKeyDown}>
          {(shops || []).map((shop) => (
            <button
              key={shop.id}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={shop.id === activeShopId ? 'stores-switcher-item is-active' : 'stores-switcher-item'}
              onClick={() => choose(shop.id)}
            >
              <span className="stores-switcher-check" aria-hidden="true">
                {shop.id === activeShopId ? <Check size={14} /> : null}
              </span>
              <span className="stores-switcher-item-name">{shop.name}</span>
              {shop.id === activeShopId && <span className="sr-only">(boutique active)</span>}
            </button>
          ))}
          <div className="stores-switcher-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className="stores-switcher-item stores-switcher-item--create"
            onClick={() => { setOpen(false); onCreate?.(); }}
          >
            <Plus size={14} aria-hidden="true" /> Créer une boutique
          </button>
        </div>
      )}
    </div>
  );
}
